import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router";
import useAccessTokenData from "./use-access-token-data";
import useMeQuery from "./use-me-query";
import usePostNextSwitchMutate from "./use-post-next-switch-mutate";
import usePostPreviousSwitchMutate from "./use-post-previous-switch-mutate";
import useUpdateMeta from "./use-update-meta";
import { UseUsersFetchParams } from "./use-users-query";

export const useUserSwitchLogic = () => {
  const accessTokenData = useAccessTokenData();
  const { data } = useMeQuery();
  const { mutateAsync } = useUpdateMeta();
  const { mutateAsync: nextMutateAsync } = usePostNextSwitchMutate();
  const { mutateAsync: previousMutateAsync } = usePostPreviousSwitchMutate();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const moveToUserList = useCallback(async (switchStatus: "success" | "return", {
    search,
    status,
    report,
    measurement
  }: Omit<NonNullable<UseUsersFetchParams["query"]>, "limit" | "offset"> = {
    search: "",
    status: "all",
    report: "all",
    measurement: "all"
  }) => {
    if (switchStatus === "success") {
      if (pathname === "/") return;

      await navigate("/");
      return;
    }
    if (pathname === "/settings/users") return;

    let filter: string;

    if (report === "with") {
      filter = "report";
    } else if (report === "without") {
      filter = "no-report";
    } else if (measurement === "with") {
      filter = "measurement";
    } else if (status === "new") {
      filter = "new";
    } else {
      filter = "all";
    }

    await navigate({
      pathname: "/settings/users",
      search: `?q=${search}&filter=${filter}`
    });
  }, [navigate, pathname]);

  const processUser = useCallback(async () => {
    if (accessTokenData?.switchInfoIndex === (accessTokenData?.switchInfoLength ?? 0) - 1) {
      throw new Error("Вже обрано останнього користувача");
    }

    try {
      console.log("Обробляємо користувача:", accessTokenData?.userId);
      if (accessTokenData?.id !== accessTokenData?.userId) {
        await mutateAsync({
          body: {
            meta: {
              ...(data.meta ?? {}),
              checkedDate: new Date().getTime()
            }
          }
        });
      }
      const { status, usersRequest } = await nextMutateAsync({});
      await moveToUserList(status, usersRequest);
      return { success: true, message: "Користувача оброблено" };
    } catch (error) {
      console.error("Помилка при обробці користувача:", error);
      throw new Error("Не вдалося обробити користувача");
    }
  }, [
    accessTokenData?.switchInfoIndex,
    accessTokenData?.switchInfoLength,
    accessTokenData?.userId,
    accessTokenData?.id,
    nextMutateAsync,
    moveToUserList,
    mutateAsync,
    data.meta
  ]);

  const returnUser = useCallback(async () => {
    if (accessTokenData?.switchInfoIndex === 0) {
      throw new Error("Вже обрано першого користувача");
    }

    try {
      console.log("Повертаємо користувача:", accessTokenData?.id);
      const { status, usersRequest } = await previousMutateAsync({});
      await moveToUserList(status, usersRequest);
      return { success: true, message: "Користувача повернуто" };
    } catch (error) {
      console.error("Помилка при поверненні користувача:", error);
      throw new Error("Не вдалося повернути користувача");
    }
  }, [accessTokenData?.switchInfoIndex, accessTokenData?.id, previousMutateAsync, moveToUserList]);

  const skipUser = useCallback(async () => {
    if (accessTokenData?.switchInfoIndex === (accessTokenData?.switchInfoLength ?? 0) - 1) {
      throw new Error("Вже обрано останнього користувача");
    }

    try {
      console.log("Пропускаємо користувача:", accessTokenData?.userId);
      const { status, usersRequest } = await nextMutateAsync({});
      await moveToUserList(status, usersRequest);
      return { success: true, message: "Користувача пропущено" };
    } catch (error) {
      console.error("Помилка при пропуску користувача:", error);
      throw new Error("Не вдалося пропустити користувача");
    }
  }, [
    accessTokenData?.switchInfoIndex,
    accessTokenData?.switchInfoLength,
    accessTokenData?.userId,
    nextMutateAsync,
    moveToUserList
  ]);

  return {
    processUser,
    returnUser,
    skipUser,
    canSwitch: accessTokenData?.userId !== accessTokenData?.id,
    currentUserId: accessTokenData?.userId,
    originalUserId: accessTokenData?.id,
  };
};
